/* eslint-disable lingui/no-unlocalized-strings -- TODO(i18n): traducir (deuda) */
/**
 * Platform adapter for the shortcut system.
 *
 * Decides:
 *   - what "Mod" means (Cmd on macOS, Ctrl elsewhere)
 *   - which key strings the host environment reserves
 *   - how a binding should render in <kbd> badges (⌘, ⇧, ⌥, Ctrl, Alt, Shift…)
 *   - how to translate a KeyboardEvent into the engine's canonical signature
 */

import { eventToSignature } from "./keys";
import { isTauriRuntime } from "@/lib/platform";
import type { Platform, PlatformAdapter } from "./types";

function readUserAgentDataPlatform(): string | undefined {
  if (typeof navigator === "undefined") {
    return undefined;
  }
  const data: unknown = Reflect.get(navigator, "userAgentData");
  if (data && typeof data === "object") {
    const platform: unknown = Reflect.get(data, "platform");
    if (typeof platform === "string") {
      return platform;
    }
  }
  return undefined;
}

function readChromeRuntimeId(): string | undefined {
  if (typeof globalThis === "undefined") {
    return undefined;
  }
  const chrome: unknown = Reflect.get(globalThis, "chrome");
  if (!chrome || typeof chrome !== "object") {
    return undefined;
  }
  const runtime: unknown = Reflect.get(chrome, "runtime");
  if (!runtime || typeof runtime !== "object") {
    return undefined;
  }
  const id: unknown = Reflect.get(runtime, "id");
  return typeof id === "string" ? id : undefined;
}

function detectMac(): boolean {
  if (typeof navigator === "undefined") {
    return false;
  }
  const uaDataPlatform = readUserAgentDataPlatform();
  if (uaDataPlatform) {
    return /mac/i.test(uaDataPlatform);
  }
  return /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);
}

function isExtensionContext(): boolean {
  return Boolean(readChromeRuntimeId());
}

function isMobileTauri(): boolean {
  if (!isTauriRuntime()) {
    return false;
  }
  return typeof navigator !== "undefined" && /Android|iPhone|iPad|iPod/.test(navigator.userAgent);
}

export function detectPlatform(): Platform {
  if (isExtensionContext()) {
    return "extension";
  }
  if (isTauriRuntime()) {
    return isMobileTauri() ? "tauri-mobile" : "tauri-desktop";
  }
  return "web";
}

const MAC_KEY_GLYPHS: Record<string, string> = {
  Mod: "⌘",
  Meta: "⌘",
  Cmd: "⌘",
  Ctrl: "⌃",
  Control: "⌃",
  Alt: "⌥",
  Option: "⌥",
  Shift: "⇧",
  Enter: "↵",
  ArrowUp: "↑",
  ArrowDown: "↓",
  ArrowLeft: "←",
  ArrowRight: "→",
  Backspace: "⌫",
  Delete: "⌦",
  Escape: "Esc",
  Tab: "Tab",
  Space: "Space",
};

const PC_KEY_GLYPHS: Record<string, string> = {
  Mod: "Ctrl",
  Meta: "Win",
  Cmd: "Ctrl",
  Ctrl: "Ctrl",
  Control: "Ctrl",
  Alt: "Alt",
  Option: "Alt",
  Shift: "Shift",
  Enter: "Enter",
  ArrowUp: "↑",
  ArrowDown: "↓",
  ArrowLeft: "←",
  ArrowRight: "→",
  Backspace: "Backspace",
  Delete: "Delete",
  Escape: "Esc",
  Tab: "Tab",
  Space: "Space",
};

function buildFormatter(isMac: boolean): (key: string) => string {
  const table = isMac ? MAC_KEY_GLYPHS : PC_KEY_GLYPHS;
  return (key) => {
    if (table[key]) {
      return table[key];
    }
    if (key.length === 1) {
      return key.toUpperCase();
    }
    return key;
  };
}

// Bindings the browser/Chrome WebExtension API will not allow us to intercept.
const EXTENSION_RESERVED = new Set<string>([
  "Mod+t",
  "Mod+w",
  "Mod+n",
  "Mod+Shift+n",
  "Mod+Shift+t",
  "Mod+l",
  "Mod+r",
  "Mod+Shift+r",
  "Mod+d",
  "Mod+Shift+w",
  "Mod+Tab",
  "Mod+Shift+Tab",
  "F11",
]);

function isMacForPlatform(platform: Platform): boolean {
  if (platform === "tauri-mobile") {
    return false;
  }
  return detectMac();
}

export function createPlatformAdapter(platform: Platform = detectPlatform()): PlatformAdapter {
  const isMac = isMacForPlatform(platform);
  const formatKey = buildFormatter(isMac);
  const reservedBindings = platform === "extension" ? EXTENSION_RESERVED : new Set<string>();
  return {
    id: platform,
    isMac,
    reservedBindings,
    formatKey,
    normalizeEvent: (event) => eventToSignature(event, isMac),
  };
}
